import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';

import moment from 'moment';

import { CakeItEasyWebUiModule, DeliveryCalendar } from '@cakeiteasy/web-ui';
import { IDropdownItem, NgCakeItEasyUtilsModule, POST_NUMBER_LENGTHS } from '@cakeiteasy/web-utils';

import { ReturnPackagingStore } from '../../services/return-packaging.store';
import { ReturnCalendarService } from '../../services/return-calendar.service';
import { IReturnPickupLocation } from '../../models/i-return-pickup-location';
import { CitiesApiService } from '../../../api-services/services/cities-api.service';
import { DeliveryApiService } from '../../../api-services/services/delivery-api.service';

/**Courier pickup. Schedules against a pickup location: the one it is handed, or one the customer
 * types here — the post code is what resolves the city, unlocks the address and the calendar, and
 * prices the pickup.*/
@Component({
  standalone: true,
  selector: 'courier-return-schedule',
  imports: [FormsModule, TranslateModule, CakeItEasyWebUiModule, NgCakeItEasyUtilsModule],
  templateUrl: './courier-return-schedule.component.html',
  styleUrls: ['./courier-return-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourierReturnScheduleComponent implements OnInit {
  /**Not used by the calendar, which is cart-scoped — only by the deliverability check, which reads
   * the bakery's delivery prices.*/
  readonly bakeryId = input.required<number>();
  readonly countryCode = input<string>('');
  readonly currency = input<string>('');
  /**Where the courier collects from, when the cart already knows. Null hands the choice to the
   * customer and opens the address block.*/
  readonly pickupLocation = input<IReturnPickupLocation>(null);
  readonly earliestDate = input<Date>(null);
  readonly heading = input<string>('');
  readonly instructions = input<string>('');

  readonly store = inject(ReturnPackagingStore);

  private readonly _calendar = inject(ReturnCalendarService);
  private readonly _citiesApi = inject(CitiesApiService);
  private readonly _deliveryApi = inject(DeliveryApiService);
  private readonly _sanitizer = inject(DomSanitizer);

  readonly calendar = signal<DeliveryCalendar>(null);
  readonly isCalendarLoading = signal(false);
  readonly isCalendarFailed = signal(false);
  readonly isPostNumberUnavailable = signal(false);

  /**What the customer has typed so far, masked but not yet resolved. `store.pickupPostNumber` only
   * ever holds a post code we have looked up and confirmed the seller serves.*/
  readonly postNumberInput = signal<string>('');

  /**ui-input-loader-wrap takes the in-flight promise itself, and ngHelper.ifChanged skips only
   * undefined — never initialise this to null.*/
  readonly postNumberBusy = signal<Promise<any>>(undefined);

  /**The address block is only for customers we were not given a location for.*/
  readonly needsAddress = computed(() => !this.pickupLocation());

  /**Nothing below the post code means anything until we know where the courier is going.*/
  readonly isPostNumberResolved = computed(() => Boolean(this.store.pickupPostNumber()));

  readonly isScheduleDisabled = computed(() => !this.isPostNumberResolved() || this.isCalendarLoading());

  readonly safeInstructions = computed(() => this._sanitizer.bypassSecurityTrustHtml(this.instructions() || ''));

  readonly timeOptions = computed<IDropdownItem[]>(() => {
    const calendar = this.calendar();
    const date = this.store.date();

    if (!calendar || !date) {
      return [];
    }

    return this._calendar
      .getCourierReturnSlots(calendar.getDate(date))
      .map((slot) => ({ id: slot.timeRange, label: slot.timeRange, ref: slot }));
  });

  public ngOnInit(): void {
    /**A location we were given outranks one the store remembers: the saved line was made against a
     * cart the customer may have changed since.*/
    if (this.pickupLocation()) {
      this.store.setPickupLocation(this.pickupLocation());
    }

    const postNumber = this.store.pickupPostNumber();

    if (!postNumber) {
      return;
    }

    this.postNumberInput.set(postNumber);
    void this._loadCalendar(this._initialMonth());
  }

  public async onPostNumberChange(value: string | number): Promise<void> {
    const postNumber = String(value ?? '').trim();

    this.postNumberInput.set(postNumber);
    this._clearResolvedPostNumber();

    if (!this._isPostNumberComplete(postNumber)) {
      return;
    }

    const lookup = this._resolveCity(postNumber);
    this.postNumberBusy.set(lookup);

    const city = await lookup;

    /**The customer typed on while the lookup was in flight, so this answer is about a post code
     * they have already replaced.*/
    if (this.postNumberInput() !== postNumber) {
      return;
    }

    if (!city) {
      this.isPostNumberUnavailable.set(true);
      return;
    }

    this.store.pickupCity.set(city);
    this.store.pickupPostNumber.set(postNumber);

    await this._loadCalendar(this._initialMonth());
  }

  public onAddressChange(address: string): void {
    this.store.pickupAddress.set(address || '');
  }

  public async onMonthNavigate(monthDate: string): Promise<void> {
    await this._loadCalendar(monthDate, this.calendar());
  }

  public onDateChange(date: string): void {
    this.store.date.set(date);
    this.store.time.set(null);
    this.store.price.set(null);
  }

  public onTimeChange(item: IDropdownItem): void {
    this.store.time.set(item ? String(item.id) : null);
    this.store.price.set(item && item.ref ? item.ref.price : null);
  }

  private _initialMonth(): string {
    return this.store.date() || moment(this.earliestDate() || new Date()).format('YYYY-MM-DD');
  }

  /**Falls back to the Norwegian length so an unset country code cannot lock the field shut.*/
  private _isPostNumberComplete(postNumber: string): boolean {
    return postNumber.length >= (POST_NUMBER_LENGTHS[this.countryCode()] || POST_NUMBER_LENGTHS.NO);
  }

  /**A new post code invalidates the city, the calendar and the price quoted from it.*/
  private _clearResolvedPostNumber(): void {
    this.isPostNumberUnavailable.set(false);
    this.isCalendarFailed.set(false);
    this.store.clearPickupLocation();
    this.calendar.set(null);
  }

  /**Null when the post code is unknown or the seller does not serve it — both come to the same
   * thing for the customer: the courier cannot collect from there.*/
  private async _resolveCity(postNumber: string): Promise<string | null> {
    const location = await this._citiesApi.searchLocationByPostNumber(this.countryCode(), postNumber);
    const city = location && (location.placeName || location.cityName);

    if (!city) {
      return null;
    }

    return (await this._isPostNumberServed(postNumber)) ? city : null;
  }

  private async _isPostNumberServed(postNumber: string): Promise<boolean> {
    try {
      const prices = await firstValueFrom(this._deliveryApi.getBakeryDeliveryPrice(this.bakeryId(), postNumber));

      return Boolean(prices && prices.length);
    } catch {
      return false;
    }
  }

  private async _loadCalendar(monthDate: string, prevCalendar?: DeliveryCalendar): Promise<void> {
    const postNumber = this.store.pickupPostNumber();

    if (!postNumber) {
      return;
    }

    this.isCalendarLoading.set(true);
    this.isCalendarFailed.set(false);

    try {
      this.calendar.set(
        await this._calendar.loadCourierReturnCalendar({
          postNumber,
          monthDate,
          prevCalendar,
        }),
      );
    } catch {
      /**Without this the datepicker just shows every day as unavailable, which reads as "the
       * courier never comes" rather than "we could not load the times".*/
      this.isCalendarFailed.set(true);
    } finally {
      this.isCalendarLoading.set(false);
    }
  }
}
